import React, { useState } from "react";
import {
  Card,
  Descriptions,
  Avatar,
  Typography,
  Tag,
  Button,
  Row,
  Col,
  Statistic,
  Upload,
  message,
  Space,
  Popconfirm,
  Modal,
} from "antd";
import {
  CarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExclamationOutlined,
  DollarOutlined,
  CalendarOutlined,
  PaperClipOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/fr";
import {
  addVehicleFiles,
  deleteVehicleFile,
  getVehicleDocumentUrl,
} from "../../api/vehicle/vehicleRequest";
import { getMessageError } from "../../utils/messageUtils";
import { getCountryNameByCode } from "../../utils/countries";
import PermissionRender from "../../components/PermissionRender";
import { handleFilename } from "../../utils";

dayjs.extend(localizedFormat);
dayjs.locale("fr");

const { Title, Text, Link } = Typography;

const VehicleInformationTab = ({
  vehicleData,
  showEditModal,
  onVehicleUpdate,
}) => {
  const {
    id,
    matricule,
    country,
    brand,
    model,
    color,
    warnings,
    infractions,
    created_at,
    updated_at,
    prefix,
    uuid,
    documents,
  } = vehicleData;

  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isAddFileModalVisible, setIsAddFileModalVisible] = useState(false);

  const totalWarnings = warnings?.length || 0;
  const totalInfractions = infractions?.length || 0;
  const totalAmount =
    (infractions?.reduce((sum, inf) => sum + parseFloat(inf.amount), 0) || 0);

  const getLastEventDate = () => {
    const allDates = [
      ...(infractions?.map((inf) => ({
        date: dayjs(inf.datetime_infraction),
        type: "infraction",
      })) || []),
      ...(warnings?.map((warn) => ({
        date: dayjs(warn.datetime_warning),
        type: "avertissement",
      })) || []),
    ];

    allDates.sort((a, b) => b.date.valueOf() - a.date.valueOf());

    if (allDates.length > 0) {
      const mostRecent = allDates[0];
      return `${mostRecent.date.format("LL")} `;
    } else {
      return "Aucun";
    }
  };

  const documentPaths = documents
    ? documents.split(",").filter((path) => path.trim() !== "")
    : [];

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Veuillez sélectionner au moins un fichier.");
      return;
    }

    const formData = new FormData();

    let filesAppendedCount = 0; // Keep track of appended files

    fileList.forEach((file, index) => {
      const fileToAppend = file instanceof File ? file : file?.originFileObj;

      if (fileToAppend instanceof File) {
        formData.append("new_files[]", fileToAppend);
        filesAppendedCount++;
      }
    });

    if (filesAppendedCount === 0 && fileList.length > 0) {
      message.error(
        "Impossible de préparer les fichiers pour le téléversement."
      );
      return; // Stop if no files could be prepared
    }
    if (filesAppendedCount !== fileList.length) {
      message.warning(
        "Certains fichiers n'ont pas pu être préparés pour le téléversement."
      );
    }

    setUploading(true);
    try {
      const updatedVehicle = await addVehicleFiles(id, formData);
      message.success("Fichier(s) ajouté(s) avec succès.");
      setFileList([]);
      setIsAddFileModalVisible(false);
      if (onVehicleUpdate) {
        onVehicleUpdate(updatedVehicle);
      }
    } catch (error) {
      // Keep the detailed error logging
      console.error("Erreur détaillée lors de l'ajout de fichiers:", error);
      let displayMessage = "Erreur lors de l'ajout de fichiers";

      if (error.response) {
        console.error("Error Response Data:", error.response.data);
        console.error("Error Response Status:", error.response.status);
        const backendError = error.response.data?.error;
        if (typeof backendError === "string") {
          displayMessage = backendError;
        } else if (typeof backendError === "object") {
          displayMessage = Object.values(backendError).flat().join(" ");
        } else {
          displayMessage = `Erreur serveur (${error.response.status})`;
        }
        message.error(displayMessage); // Use message.error directly
      } else if (error.request) {
        console.error("Error Request:", error.request);
        displayMessage = "Aucune réponse reçue du serveur.";
        message.error(displayMessage);
      } else {
        console.error("Error Message:", error.message);
        displayMessage = `Erreur de configuration: ${error.message}`;
        message.error(displayMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (filePathToDelete) => {
    try {
      const updatedVehicle = await deleteVehicleFile(id, filePathToDelete);
      message.success("Fichier supprimé avec succès.");
      if (onVehicleUpdate) {
        onVehicleUpdate(updatedVehicle);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du fichier:", error);
      getMessageError(
        error.response?.data?.error,
        "Erreur lors de la suppression du fichier"
      );
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ].includes(file.type);

      const isLessThan10MB = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error(`${file.name} n'est pas un type de fichier supporté`);
        return Upload.LIST_IGNORE;
      }

      if (!isLessThan10MB) {
        message.error(`${file.name} dépasse 10MB`);
        return Upload.LIST_IGNORE;
      }

      const processedFile = handleFilename(file);

      if (file.name !== processedFile.name) {
        message.info(
          `Fichier "${file.name}" renommé en "${processedFile.name}"`
        );
      }

      setFileList([...fileList, file]);
      return false;
    },
    fileList,
    multiple: true,
  };

  return (
    <div className="p-4">
      <Row gutter={[24, 24]} className="mb-6">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            className="shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-indigo-50 to-white"
            bordered={false}
          >
            <Statistic
              title="Avertissements"
              value={totalWarnings}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<ExclamationCircleOutlined />}
              className="font-medium"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            className="shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-rose-50 to-white"
            bordered={false}
          >
            <Statistic
              title="Infractions"
              value={totalInfractions}
              valueStyle={{ color: "#f5222d" }}
              prefix={<FileExclamationOutlined />}
              className="font-medium"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            className="shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-green-50 to-white"
            bordered={false}
          >
            <Statistic
              title="Montant Total"
              value={totalAmount.toFixed(2)}
              suffix="MAD"
              valueStyle={{ color: "#52c41a" }}
              prefix={<DollarOutlined />}
              className="font-medium"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card
            className="shadow-md hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-blue-50 to-white"
            bordered={false}
          >
            <Statistic
              title="Dernier événement"
              value={getLastEventDate()}
              valueStyle={{ color: "#1677ff" }}
              prefix={<CalendarOutlined />}
              className="font-medium"
            />
          </Card>
        </Col>
      </Row>

      <Card
        className="shadow-md hover:shadow-lg transition-all duration-300 border-0 mb-6"
        bordered={false}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Avatar
              size={64}
              icon={<CarOutlined />}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 mr-4 shadow-md"
            />
            <div>
              <Title level={3} className="m-0 text-indigo-800">
                {matricule}
              </Title>
              <Text className="text-gray-500 text-lg">
                {brand} {model || ""}
              </Text>
            </div>
          </div>
          <PermissionRender requiredPermission={["vehicles", "update"]}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={showEditModal}
              className="bg-indigo-600 hover:bg-indigo-700 border-0 shadow-md"
            >
              Modifier Infos
            </Button>
          </PermissionRender>
        </div>
        <Descriptions
          bordered
          column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
          className="bg-white rounded-lg shadow-sm"
          labelStyle={{ fontWeight: "600" }}
          size="middle"
        >
          <Descriptions.Item label="Immatriculation">
            <Text strong className="text-lg">
              {matricule}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Pays">
            <div className="flex items-center">
              <GlobalOutlined className="mr-2 text-gray-500" />
              {getCountryNameByCode(country) || "Non spécifié"}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Marque">
            <Tag color="blue" className="text-md px-3 py-1">
              {brand || "Non spécifié"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Modèle">
            <Tag color="cyan" className="text-md px-3 py-1">
              {model || "Non spécifié"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Couleur">
            {color ? (
              <div className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-2"
                  style={{ backgroundColor: color }}
                ></div>
                <span>{color}</span>
              </div>
            ) : (
              "Non spécifié"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date de création">
            <div className="flex items-center">
              <CalendarOutlined className="mr-2 text-gray-500" />
              {created_at ? dayjs(created_at).format("LLL") : "N/A"}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Dernière mise à jour">
            <div className="flex items-center">
              <CalendarOutlined className="mr-2 text-gray-500" />
              {updated_at ? dayjs(updated_at).format("LLL") : "N/A"}
            </div>
          </Descriptions.Item>

          {prefix && uuid && (
            <Descriptions.Item label="UUID Véhicule">
              <Tag>{prefix}</Tag> {uuid}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card
        title="Documents Associés"
        className="shadow-md hover:shadow-lg transition-all duration-300 border-0"
        bordered={false}
        extra={
          <PermissionRender requiredPermission={["vehicles", "update"]}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setIsAddFileModalVisible(true)}
            >
              Ajouter Fichier
            </Button>
          </PermissionRender>
        }
      >
        {documentPaths.length > 0 ? (
          <Space direction="vertical" className="w-full">
            {documentPaths.map((filePath, index) => {
              const filename = filePath.split("/").pop();
              const fileUrl = getVehicleDocumentUrl(filePath);
              return (
                <div
                  key={index}
                  className="flex justify-between items-center p-2 border-b last:border-b-0"
                >
                  <Link
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center"
                  >
                    <PaperClipOutlined className="mr-2" />
                    {filename}
                  </Link>
                  <PermissionRender requiredPermission={["vehicles", "update"]}>
                    <Popconfirm
                      title="Supprimer ce fichier ?"
                      onConfirm={() => handleDeleteFile(filePath)}
                      okText="Oui"
                      cancelText="Non"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                      />
                    </Popconfirm>
                  </PermissionRender>
                </div>
              );
            })}
          </Space>
        ) : (
          <Text type="secondary">Aucun document associé à ce véhicule.</Text>
        )}
      </Card>

      <Modal
        title="Ajouter des Fichiers"
        open={isAddFileModalVisible}
        onCancel={() => {
          setIsAddFileModalVisible(false);
          setFileList([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsAddFileModalVisible(false);
              setFileList([]);
            }}
          >
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 border-0 text-white"
          >
            {uploading ? "Téléversement..." : "Ajouter"}
          </Button>,
        ]}
      >
        <Upload
          {...uploadProps}
          listType="picture"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
        >
          <Button icon={<UploadOutlined />}>Sélectionner Fichiers</Button>
        </Upload>
        <Text type="secondary" style={{ display: "block", marginTop: "8px" }}>
          Vous pouvez sélectionner plusieurs fichiers. Support pour PDF, Word,
          Excel, images (JPG, PNG, GIF) et TXT. Maximum 10MB par fichier.
        </Text>
      </Modal>
    </div>
  );
};

export default VehicleInformationTab;
